import { CalculationMetaApi } from "src/app/types";
import { CalculationRow, Margin, NonBreakingRows, PdfExportOptions, PdfExportParagraph } from "./pdf-export.types";
import { ViewModelLineType } from "src/app/state-management/calculation-state";

export class CalculationTableBuilder {
    private partitions: [number, number][] = [];
    private rows: CalculationRow[] = [];

    constructor(private meta: CalculationMetaApi, private options: PdfExportOptions, private canvasHeight: number) { }

    public addRow(cells: PdfExportParagraph[], lineType: ViewModelLineType, margins?: Margin): CalculationTableBuilder {
        const rowHeight = Math.max(...cells.map(cell => cell.paragraphDimensions.height));
        const row: CalculationRow = {
            lineType: lineType,
            cells: cells.map(cell => ({ ...cell, paragraphDimensions: { ...cell.paragraphDimensions, height: rowHeight } })),
            dimensions: {
                width: cells.reduce((sum, cell) => sum + cell.paragraphDimensions.width, 0),
                height: rowHeight
            },
            margins
        };

        this.rows.push(row);

        return this;
    }

    private placeOnPage(rows: CalculationRow[]): CalculationRow[] {
        // Give all cells the exact y coordinates based on the row height and margins
        let currentY = 0;
        return rows.map(row => {
            const cellsWithY = row.cells.map(cell => ({
                ...cell, paragraphDimensions: { ...cell.paragraphDimensions, y: currentY }
            }));
            currentY += (row.dimensions?.height ?? 0) + (row.margins?.top ?? 0) + (row.margins?.bottom ?? 0);
            return { ...row, cells: cellsWithY };
        });
    }

    public build(): CalculationRow[][] {
        const result: CalculationRow[][] = [];

        console.log(this.rows);

        while (this.rows.length > 0) {
            // Get enough rows to fill the page
            let rowsHeight = 0;
            let nextPageBreakIndex = this.rows.findIndex(row => {
                // Add height
                rowsHeight += (row.dimensions?.height ?? 0) + (row.margins?.top ?? 0) + (row.margins?.bottom ?? 0);

                // Check if we exceeded the page height
                return rowsHeight > this.canvasHeight;
            })

            if (nextPageBreakIndex === -1) {
                // All rows fit on one page
                result.push(this.placeOnPage(this.rows));
                this.rows = [];
            } else {
                // Check if some of the lines need to be moved to the next page based on the break strategy
                // Simple rules
                // 1. If the last line is a category or subcategory, move it to the next page
                const isCategoryOrSubcategory = (line: CalculationRow) => [ViewModelLineType.CATEGORY, ViewModelLineType.SUBCATEGORY].includes(line.lineType);
                for (
                    let original = nextPageBreakIndex;
                    isCategoryOrSubcategory(this.rows[nextPageBreakIndex - 1])
                    && original - nextPageBreakIndex < 3;
                    nextPageBreakIndex--
                ) { };
                // 2. If the first line on the next page is a subtotal or total, move a few lines to the next page
                const isSubtotalOrTotal = (line: CalculationRow) => [ViewModelLineType.SUBTOTAL, ViewModelLineType.TOTAL].includes(line.lineType);
                for (
                    let original = nextPageBreakIndex;
                    isSubtotalOrTotal(this.rows[nextPageBreakIndex])
                    && nextPageBreakIndex - original < 3;
                    nextPageBreakIndex--
                ) { }
                // 3. Keep the 'staart' lines together
                const isTail = (line: CalculationRow) => [ViewModelLineType.TAIL_AMOUNT, ViewModelLineType.TAIL_PERCENTAGE, ViewModelLineType.TAIL_TEXT, ViewModelLineType.TAIL_PRELIMANARY, ViewModelLineType.TAIL_SUBTOTAL, ViewModelLineType.TOTAL,ViewModelLineType.CATEGORY, ViewModelLineType.SUBCATEGORY].includes(line.lineType);
                // Count the number of tail lines at the end of the page
                const onThisPage = [...this.rows.slice(0, nextPageBreakIndex).reverse()].findIndex(line => !isTail(line));

                if (onThisPage > 0) {
                    let onNextPage = [...this.rows.slice(nextPageBreakIndex)].findIndex(line => !isTail(line));
                    if (onNextPage === -1) {
                        onNextPage = this.rows.length - nextPageBreakIndex;
                    }

                    const onlyAFewOnNextPage = onNextPage < 6;
                    const onlyAFewOnThisPage = onThisPage < 6;
                    const keepOnThisPage = (onThisPage > 20) || (onNextPage === 0);

                    console.log('Tail lines on this page:', onThisPage, 'tail lines on next page:', onNextPage, 'only a few on next page:', onlyAFewOnNextPage, 'only a few on this page:', onlyAFewOnThisPage, 'keep on this page:', keepOnThisPage);

                    if (!keepOnThisPage) {
                        if (onlyAFewOnNextPage || onlyAFewOnThisPage) {
                            nextPageBreakIndex -= onThisPage;
                        }
                    }
                }

                // Count the number of tail lines at the next page
                result.push(this.placeOnPage(this.rows.slice(0, nextPageBreakIndex)));
                this.rows = this.rows.slice(nextPageBreakIndex);
            }
        }
        
        return result;
    }

    // Calculate the page brakes
    public buildOld(): CalculationRow[][] {
        const pages: NonBreakingRows[][] = [];
        let currentPage: NonBreakingRows[] = [];
        let canvasHeightLeft = this.canvasHeight;

        // Merge all partitions into a single array of non-overlapping groups
        this.partitions.sort((a, b) => a[0] - b[0]); // Sort by start index
        let mergedPartitions: [number, number][] = [];

        // Cap partition values
        this.partitions = this.partitions.map(partition => [
            Math.max(0, partition[0]),
            Math.min(this.rows.length, partition[1])
        ]);

        // console.log('Partitions before merging:', this.partitions);

        for (const [start, end] of this.partitions) {
            if (mergedPartitions.length === 0 || mergedPartitions[mergedPartitions.length - 1][1] < start) {
                // No overlap, add new partition
                mergedPartitions.push([start, end]);
            } else {
                // Overlap, merge with the last partition
                mergedPartitions[mergedPartitions.length - 1][1] = Math.max(end, mergedPartitions[mergedPartitions.length - 1][1]);
            }
        }

        let rowNr = 0;

        const finalizePage = () => {
            // console.log('Finalizing page with', currentPage.length, 'groups');
            if (currentPage.length > 0) {
                // If there are rows in the current group, add them
                pages.push(currentPage);
            }
            currentPage = [];
            canvasHeightLeft = this.canvasHeight;
        }

        const useGroups = [
            ...mergedPartitions,
            // Add a final entry to ensure the last rows are processed
            [this.rows.length, this.rows.length]
        ];

        // console.log('Merged partitions:', useGroups);

        for (const [start, end] of useGroups) {
            // console.log('Processing group from row', start, 'to', end, 'with value', rowNr);

            // All up to the current index will have one row per group
            while (start > rowNr) {
                // console.log('Adding row', rowNr, 'to current page');

                // Check space left on the current page
                const row = this.rows[rowNr];
                if (canvasHeightLeft < (row.dimensions?.height ?? 0)) {
                    finalizePage();
                }

                // Make sure the cell heights are set correctly
                for (const cell of row.cells) {
                    cell.paragraphDimensions.height = (row.dimensions?.height ?? 0);
                }

                // Add row to the current page
                currentPage.push({
                    rows: [row],
                    dimensions: { width: (row.dimensions?.width ?? 0), height: (row.dimensions?.height ?? 0) }
                });

                canvasHeightLeft -= (row.dimensions?.height ?? 0);

                rowNr++;
            }

            // console.log(`Canvas height left: ${canvasHeightLeft}, current row number: ${rowNr}`);

            // Now all rows up to the current index are added, we can add the group
            if (end > start) { // Exclude the last group which is always 0
                // Check if we can fit the group in the current page
                const groupHeight = this.rows.slice(start, end + 1).reduce((sum, row) => sum + (row.dimensions?.height ?? 0), 0); // Include item at `end`
                if (canvasHeightLeft < groupHeight) {
                    finalizePage();
                }

                // Add the group to the current page
                currentPage.push({
                    rows: this.rows.slice(start, end + 1).map(row => ({
                        ...row,
                        cells: row.cells.map(cell => ({
                            ...cell,
                            paragraphDimensions: {
                                ...cell.paragraphDimensions,
                                height: (row.dimensions?.height ?? 0) // Ensure all cells in the group have the same height
                            }
                        }))
                    })),
                    dimensions: {
                        width: this.rows.slice(start, end + 1).reduce((sum, row) => Math.max(sum, (row.dimensions?.width ?? 0)), 0),
                        height: groupHeight
                    }
                });

                canvasHeightLeft -= groupHeight;
                rowNr = end + 1; // Move the row number forward by the size of the group
            }

            // console.log(`Canvas height left: ${canvasHeightLeft}, current row number: ${rowNr}`);
        }

        // Add last group
        if (currentPage.length > 0) {
            finalizePage();
        }

        const splitByPage = pages.map(page => page.flatMap(group => group.rows));

        // console.log('Split by page:', splitByPage);

        return splitByPage;
    }
}